import { useMemo } from "react";
import { ArrowUpRight, ArrowDownLeft, Banknote, Building2, FileCheck, CreditCard, CalendarClock } from "lucide-react";
import { cn } from "@/utils/Functions";
import { Card, CardHeader, CardTitle, CardPanel } from "@/components/ui/card";
import type { Transaction } from "@/types/portfolio.types";

const methodIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  wire: Banknote,
  ach: Building2,
  check: FileCheck,
  platform_escrow: CreditCard,
};

const methodLabels: Record<string, string> = {
  wire: "Wire",
  ach: "ACH",
  check: "Check",
  platform_escrow: "Escrow",
};

type Props = {
  transactions: Transaction[];
};

export function UpcomingPayments({ transactions }: Props) {
  const upcoming = useMemo(
    () =>
      transactions
        .filter((t) => t.status === "scheduled" || t.status === "pending")
        .sort((a, b) => a.isoDate.localeCompare(b.isoDate)),
    [transactions],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Upcoming Payments</CardTitle>
      </CardHeader>
      <CardPanel>
      {upcoming.length > 0 ? (
        <div className="space-y-2">
          {upcoming.map((tx) => {
            const MethodIcon = tx.method ? methodIcons[tx.method] : null;
            return (
              <div
                key={tx.id}
                className="flex items-center gap-3 p-3 rounded-xl border border-border/50 hover:bg-muted/30 transition-colors"
              >
                <div className={cn(
                  "size-8 rounded-full grid place-items-center shrink-0",
                  tx.direction === "in" ? "bg-brand-green/10 text-brand-green" : "bg-brand-amber/10 text-brand-amber"
                )}>
                  {tx.direction === "in" ? <ArrowDownLeft className="size-3.5" /> : <ArrowUpRight className="size-3.5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground">{tx.title}</div>
                  <div className="text-xs text-muted-foreground">{tx.counterparty}</div>
                </div>
                {MethodIcon && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full text-muted-foreground bg-muted/50 flex items-center gap-1">
                    <MethodIcon className="size-3" />
                    {methodLabels[tx.method!]}
                  </span>
                )}
                <span className="text-xs text-muted-foreground">{tx.date}</span>
                <span className={cn(
                  "text-sm font-semibold tabular-nums",
                  tx.direction === "in" ? "text-emerald-500" : "text-foreground"
                )}>
                  {tx.direction === "in" ? "+" : "-"}${tx.amount.toLocaleString()}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <CalendarClock className="size-8 text-muted-foreground/50 mb-2" />
          <p className="text-sm text-muted-foreground">No upcoming payments scheduled</p>
        </div>
      )}
      </CardPanel>
    </Card>
  );
}
